import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND, NAV_LINKS } from "@/lib/brand";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#050505]/85 border-b border-white/10"
          : "bg-gradient-to-b from-[#050505]/95 via-[#050505]/70 to-transparent"
      }`}
      data-testid="main-nav"
    >
      {/* Top masthead band — full title across the top */}
      <div
        className={`border-b border-white/5 transition-all duration-500 ${
          scrolled ? "py-1.5" : "py-3 sm:py-4"
        }`}
        data-testid="nav-masthead"
      >
        <a
          href="#home"
          className="block text-center px-4"
          data-testid="nav-masthead-title"
        >
          <span
            className={`font-script text-brand leading-none whitespace-nowrap transition-all duration-500 ${
              scrolled
                ? "text-2xl sm:text-3xl"
                : "text-3xl sm:text-5xl lg:text-6xl"
            }`}
          >
            Crown Jewels MC Houston
          </span>
          {!scrolled && (
            <span className="block font-sans text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-white/55 mt-2">
              Sisterhood · Est. {BRAND.mcEst}
            </span>
          )}
        </a>
      </div>

      {/* Main nav row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <a
          href="#home"
          className="flex items-center gap-3 group shrink-0"
          data-testid="nav-logo"
          aria-label="Crown Jewels Sisterhood Houston"
        >
          <img
            src={BRAND.logoImage}
            alt="Crown Jewels logo"
            className="h-10 sm:h-11 w-auto object-contain drop-shadow-[0_0_14px_rgba(233,30,140,0.35)] transition-transform duration-500 group-hover:scale-[1.05]"
            data-testid="nav-logo-image"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.slice(1, -1).map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-white/80 hover:text-brand transition-colors relative group whitespace-nowrap"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-brand transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            asChild
            className="bg-brand hover:bg-brand-hover text-white rounded-none px-3 sm:px-6 text-xs sm:text-sm font-semibold tracking-wide whitespace-nowrap"
            data-testid="nav-join-cta"
          >
            <a href="#join">
              <span className="sm:hidden">Apply Now</span>
              <span className="hidden sm:inline">Join the Sisterhood</span>
            </a>
          </Button>

          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10 hover:text-brand rounded-none"
                  data-testid="nav-mobile-trigger"
                  aria-label="Open navigation menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0a0a0a] border border-white/10 rounded-none min-w-[220px]"
                data-testid="nav-mobile-menu"
              >
                {NAV_LINKS.map((link) => (
                  <DropdownMenuItem
                    asChild
                    key={link.href}
                    className="text-white/80 hover:text-brand focus:text-brand focus:bg-white/5 rounded-none cursor-pointer"
                  >
                    <a
                      href={link.href}
                      data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {link.label}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
